/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable max-len */
/* eslint-disable no-bitwise */
import { SgpCommandCode, SgpFlagMode } from './general.model';
import { ControlWordDecodeParams, CommandFieldParams } from './response.model';

/**
 * SGP (Sofar General Protocol) model
 *
 * @class SgpSupport
 */
export class SgpSupport {

  /**
   *
   * @param mode SgpFlagMode
   * @returns Normal mode, flag is fixed as 0x47<G>, 0x45<E> OR Advanced mode, flag is fixed as 0x41<A>、0x44<D>.
   */
  static FlagCode(mode: SgpFlagMode = SgpFlagMode.Normal): Uint8Array {
    if (mode === SgpFlagMode.Normal) {
      return new Uint8Array([0x47, 0x45]);
    }
    return new Uint8Array([0x41, 0x44]);
  }
  static GetFlagMode(data: Uint8Array): SgpFlagMode {
    if (data[0] === 0x47 && data[1] === 0x45) {
      return SgpFlagMode.Normal;
    } else if (data[0] === 0x41 && data[1] === 0x44) {
      return SgpFlagMode.Advanced;
    }
    throw new Error('Invalid Mode');
  }

  /**
   * Format the control word
   * Reserved (bit 15 - bit 12) | Frame format version (bit 11 - bit 8) | Encrypted (bit 7) | Need response (bit 6) | Frame Number (bit 5 - bit 3) | Address length (bit 2 - bit 0)
   *
   * @Note
   *   The frame format version is used to distinguish the different versions of the frame format. The default value is 1.
   *   The frame number is used for protocol interaction to distinguish previous and following frames.
   */
  static ControlWord(
    isEncrypted: boolean,
    needResponse: boolean,
    frameNumber: number,
    addressLength: number,
    frameFormatVersion: number = 1,
  ): Uint8Array {
    const controlWord = new Uint16Array(1);
    controlWord[0] = frameFormatVersion << 8;
    if (isEncrypted) {
      controlWord[0] |= 0x80;
    }
    if (needResponse) {
      controlWord[0] |= 0x40;
    }
    controlWord[0] |= (frameNumber << 3) & 0x38;
    controlWord[0] |= addressLength & 0x07;
    return new Uint8Array(controlWord.buffer);
  }
  static GetControlWordParams(data: Uint8Array): ControlWordDecodeParams {
    return {
      frameFormatVersion: (data[0] & 0xf0) >> 4,
      isEncrypted: (data[0] & 0x80) >> 7 === 1,
      needResponse: (data[0] & 0x40) >> 6 === 1,
      frameNumber: (data[0] & 0x38) >> 3,
      addressLength: data[1] & 0x07,
    };
  }

  /**
   * Length indication field -> 2 bytes (16 bits)
   *
   * @param info Command information field
   * @param addressField Address field
   * @returns Length indication field indicates the length of (Address field) +information field.
   */
  static LengthIndicationField(info: Uint8Array, addressField: number = 0): Uint8Array {
    const length = new Uint8Array(2);
    length[0] = (info.length + addressField) >> 8;
    length[1] = (info.length + addressField) & 0xff;
    return length.reverse();
  }

  static GetLengthIndicationField(data: Uint8Array): number {
    return data.reverse().reduce((a, b) => (a << 8) + b);
  }

  /**
   * Build command field
   *  b^15: 0 Master to slave, 1 Slave to master
   *  b^14 ~ b^0: Command code - SgpCommandCode
   */
  static CommandField(command: SgpCommandCode, opts: {
    masterToSlave: boolean;
  }): Uint8Array {
    const commandField = new Uint8Array(2);
    commandField[0] = command & 0xff;
    commandField[1] = (command >> 8) & 0xff;
    if (opts.masterToSlave === false) {
      commandField[1] |= 0x80;
    }
    return commandField;
  }
  static GetCommandField(data: Uint8Array): CommandFieldParams {
    const masterToSlave = (data[1] & 0x80) >> 7 === 0;
    data[1] &= 0x7f; // clear bit 15
    const commandCode = data[0] | (data[1] << 8);
    return {
      commandCode,
      masterToSlave,
    };
  }

  static ConvertToString(data: Uint8Array): string {
    const chars = new Array(data.length);
    for (let i = 0; i < data.length; i++) {
      chars[i] = String.fromCharCode(data[i]);
    }
    return chars.join('');
  }

  /**
   * The bit0-bit7 of the first byte represents packets 1 to 8 of the firmware, and so on.
   * 1: Receive failed
   * 0: Received successfully
   */
  static GetFailedPackets(data: Uint8Array): number[] {
    const failedPackets = [];
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < 8; j++) {
        const packetNumber = i * 8 + j + 1;
        if ((data[i] & (1 << j)) !== 0) {
          failedPackets.push(packetNumber);
        }
      }
    }
    return failedPackets;
  }

  static QueryFrame(data: ArrayBuffer, offset: number, length: number): Uint8Array {
    const dataView = new DataView(data);
    const frame = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      frame[i] = dataView.getUint8(offset + i);
    }
    return frame;
  }
}
